import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideNgReflectAttributes, provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData, ViewportScroller } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { appRoutes } from '@core/routes/app.routes';
import { coreInterceptorProviders, rsNumberInterceptor } from '@core/http-interceptors';
import { UserService } from '@core/services/user.service';
import { MrtAmplitudeService } from '@app/shared/services/shared/amplitude/mrt-amplitude.service';
import { RsCoreModule, ScrollToModule, UserInfoCookieAppName } from '@lib/rs-ngx';
import { environment } from '@environments/environment';
import { provideRsAmplitude } from '@rs-shared-lib/providers/amplitude-provider';

// Registering locale data to be able to use it for numbers and dates
registerLocaleData(localeFr);
registerLocaleData(localeNl);
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withComponentInputBinding(),
    ),
    provideAppInitializer(() => inject(ViewportScroller).setOffset([0, 64])),
    importProvidersFrom(
      ScrollToModule.forRoot(),
      RsCoreModule.forRoot({
        appName: UserInfoCookieAppName.MRT,
        environment,
      }),
    ),
    provideAnimations(),
    coreInterceptorProviders,
    provideNgReflectAttributes(),
    provideHttpClient(
      withInterceptors([rsNumberInterceptor]),
      withInterceptorsFromDi(),
    ),
    provideAppInitializer(() => inject(UserService).init()),
    provideRsAmplitude(MrtAmplitudeService),
    provideAppInitializer(() => {
      const translateService = inject(TranslateService);
      const dateAdapter = inject(DateAdapter<Date>);

      translateService.onLangChange.subscribe(({ lang }) => {
        dateAdapter.setLocale(lang);
      });
    }),
    provideAppInitializer(() => {
      const matIconRegistry = inject(MatIconRegistry);
      const domSanitizer = inject(DomSanitizer);
      const customIconsFolder = 'assets/img/custom-mat-icons/';

      [
        'auto_towing',
        'build',
        'directions_car',
        'download',
        'settings',
        'sun_snow',
        'sun_snow_multicolor',
        'sunny_outline',
        'tire_22',
      ].forEach((iconName) => {
        matIconRegistry.addSvgIcon(
          iconName,
          domSanitizer.bypassSecurityTrustResourceUrl(`${customIconsFolder}${iconName}.svg`),
        );
      });

      const sharedIconsFolder = 'assets/icons/';
      [
        'check-circle-alert',
        'file-outline',
        'king',
        'person',
      ].forEach((iconName) => {
        matIconRegistry.addSvgIcon(
          iconName,
          domSanitizer.bypassSecurityTrustResourceUrl(`${sharedIconsFolder}${iconName}.svg`),
        );
      });
    }),
  ],
};
